import React, { useState } from "react";
import { supabase } from "../supabaseClient";
import { AlertCircle, CheckCircle2, Package, Clock, ChevronRight, ArrowLeft } from "lucide-react";

const API_URL = "https://i-kept-all-the-code-in-github-and-now-i.onrender.com";

// ── helpers ──────────────────────────────────────────────────────────────────
const daysSince = (dateStr) => {
  if (!dateStr) return 999;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

// ── Policy Q&A ────────────────────────────────────────────────────────────────
const POLICY_QUESTIONS = [
  {
    id: "reason",
    question: "What is the reason for your return?",
    options: [
      { value: "damaged", label: "Product received is damaged" },
      { value: "defect", label: "Manufacturing defect" },
      { value: "color", label: "Color/texture difference from screen" },
      { value: "mind", label: "Change of mind" },
      { value: "size", label: "Size issue" },
      { value: "other", label: "Other" },
    ],
    validValues: ["damaged", "defect"],
    invalidMsg: {
      color: "Returns are not accepted for natural colour/texture variations or differences due to screen settings. This is a characteristic of eco-friendly, plant-based materials.",
      mind: "Returns are not accepted for change of mind after purchase.",
      size: "Returns are not accepted for size issues. Please refer to the size guide before ordering.",
      other: "This reason does not qualify for a return under our policy.",
    },
  },
  {
    id: "condition",
    question: "What is the condition of the product?",
    options: [
      { value: "unused_original", label: "Unused, in original packaging/condition" },
      { value: "unused_open", label: "Unused, but packaging opened" },
      { value: "used", label: "Used/worn" },
    ],
    validValues: ["unused_original"],
    invalidMsg: {
      unused_open: "Product must be in its original, unopened condition to be eligible for replacement.",
      used: "Returns/replacements are not accepted for used or worn products.",
    },
  },
  {
    id: "proof",
    question: "Do you have clear images or video proof of the damage/defect?",
    options: [
      { value: "yes", label: "Yes, I have clear photo/video proof" },
      { value: "no", label: "No, I don't have proof" },
    ],
    validValues: ["yes"],
    invalidMsg: {
      no: "You must provide clear images or videos as proof of damage or defect to proceed with a return/replacement claim.",
    },
  },
];

// ── Main Component ────────────────────────────────────────────────────────────
const ReturnOrder = () => {
  const [step, setStep] = useState("email"); // email → orders → policy → confirm → done
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [policyStep, setPolicyStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [invalidReason, setInvalidReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [returnId, setReturnId] = useState(null);

  // ── Step 1: Fetch orders by email ─────────────────────────────────────────
  const fetchOrders = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmailLoading(true);
    setEmailError("");

    const { data, error } = await supabase
      .from("orders")
      .select("id, created_at, delivered_at, status, total_amount, items, customer_name, tracking_number")
      .eq("email", email.trim().toLowerCase())
      .in("status", ["delivered", "paid", "shipped", "pending", "processing"])
      .order("created_at", { ascending: false });

    setEmailLoading(false);

    if (error) { setEmailError("Something went wrong. Please try again."); return; }
    if (!data || data.length === 0) { setEmailError("No orders found for this email address."); return; }

    setOrders(data);
    setStep("orders");
  };

  // ── Step 2: Select order ──────────────────────────────────────────────────
  const selectOrder = (order) => {
    setSelectedOrder(order);
    setAnswers({});
    setPolicyStep(0);
    setInvalidReason("");
    setStep("policy");
  };

  // ── Step 3: Policy Q&A ────────────────────────────────────────────────────
  const handleAnswer = (questionId, value) => {
    const question = POLICY_QUESTIONS[policyStep];
    setAnswers(prev => ({ ...prev, [questionId]: value }));

    if (!question.validValues.includes(value)) {
      const msg = question.invalidMsg[value] || "This reason does not qualify under our return policy.";
      setInvalidReason(msg);
      return;
    }

    setInvalidReason("");
    if (policyStep < POLICY_QUESTIONS.length - 1) {
      setPolicyStep(prev => prev + 1);
    } else {
      setStep("confirm");
    }
  };

  // ── Step 4: Submit return ─────────────────────────────────────────────────
  const submitReturn = async () => {
    if (!selectedOrder) return;
    setSubmitting(true);

    const reasonMap = { damaged: "Product Damaged", defect: "Manufacturing Defect" };
    const reason = reasonMap[answers.reason] || answers.reason;

    const { data: ret, error } = await supabase.from("returns").insert([{
      order_id: selectedOrder.id,
      reason,
      product_condition: answers.condition,
      has_proof: answers.proof === "yes",
      is_valid: null, // pending admin review
      customer_email: email.trim().toLowerCase(),
    }]).select().single();

    setSubmitting(false);

    if (error) { alert("Failed to submit return. Please try again."); return; }

    setReturnId(ret.id);
    setStep("done");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bg-cream min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-serif text-4xl text-charcoal mb-2">Return Order</h1>
          <p className="text-sm text-[#6b665f]">Siyaanish – Live Your Values</p>
        </div>

        {/* ── STEP: EMAIL ── */}
        {step === "email" && (
          <div className="bg-white border border-[#EAE4DC] rounded-2xl p-8 shadow-sm">
            <h2 className="font-serif text-2xl text-charcoal mb-2">Find Your Orders</h2>
            <p className="text-sm text-[#6b665f] mb-6">Enter the email address used when placing your order.</p>
            <form onSubmit={fetchOrders} className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-[#ba8245] focus:outline-none text-charcoal"
                />
              </div>
              {emailError && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle size={16} /> {emailError}
                </div>
              )}
              <button
                type="submit"
                disabled={emailLoading}
                className="w-full bg-[#ba8245] text-white py-3 rounded-full shadow hover:bg-[#a8733d] transition-all uppercase tracking-wider text-sm font-medium disabled:opacity-50"
              >
                {emailLoading ? "Searching..." : "Find My Orders"}
              </button>
            </form>
          </div>
        )}

        {/* ── STEP: ORDERS LIST ── */}
        {step === "orders" && (
          <div>
            <button
              onClick={() => setStep("email")}
              className="flex items-center gap-2 text-sm text-[#6b665f] mb-6 hover:text-[#ba8245] transition-colors"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <h2 className="font-serif text-2xl text-charcoal mb-2">Your Orders</h2>
            <p className="text-sm text-[#6b665f] mb-6">Select an order to initiate a return. Returns must be reported within 7 days of delivery.</p>
            <div className="space-y-4">
              {orders.map((order) => {
                const deliveredDate = order.delivered_at;
                const days = daysSince(deliveredDate);
                const isDelivered = order.status === "delivered";
                const withinWindow = isDelivered && days <= 7;
                const canReturn = withinWindow;
                const daysLeft = isDelivered ? Math.max(0, 7 - days) : null;

                return (
                  <div
                    key={order.id}
                    className={`bg-white border rounded-xl p-5 shadow-sm ${canReturn ? "border-[#EAE4DC]" : "border-[#EAE4DC] opacity-70"}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-medium text-charcoal">Order #{order.id}</div>
                        <div className="text-xs text-[#9b9590] mt-1">Placed: {formatDate(order.created_at)}</div>
                        {deliveredDate && <div className="text-xs text-[#9b9590]">Delivered: {formatDate(deliveredDate)}</div>}
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-[#ba8245]">₹{parseFloat(order.total_amount || 0).toLocaleString("en-IN")}</div>
                        <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                          order.status === "delivered" ? "bg-green-100 text-green-700" :
                          order.status === "paid" ? "bg-blue-100 text-blue-700" :
                          "bg-gray-100 text-gray-600"
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>

                    {/* Items preview */}
                    {Array.isArray(order.items) && order.items.length > 0 && (
                      <div className="text-xs text-[#6b665f] mb-3 flex gap-2 flex-wrap">
                        {order.items.slice(0, 3).map((item, i) => (
                          <span key={i} className="bg-[#f8f4ef] px-2 py-0.5 rounded-full">
                            {item.product_name || item.name}
                          </span>
                        ))}
                        {order.items.length > 3 && <span className="text-[#ba8245]">+{order.items.length - 3} more</span>}
                      </div>
                    )}

                    {/* Return eligibility */}
                    {!isDelivered ? (
                      <div className="flex items-center gap-2 text-xs text-[#9b9590]">
                        <Clock size={12} />
                        Return available only after delivery
                      </div>
                    ) : days > 7 ? (
                      <div className="flex items-center gap-2 text-xs text-red-500">
                        <AlertCircle size={12} />
                        Return window expired (7-day limit from delivery)
                      </div>
                    ) : (
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 size={12} /> {daysLeft} day{daysLeft !== 1 ? "s" : ""} left in return window
                        </span>
                        <button
                          onClick={() => selectOrder(order)}
                          className="flex items-center gap-1 bg-[#ba8245] text-white text-xs px-4 py-2 rounded-full hover:bg-[#a8733d] transition-colors"
                        >
                          Return This Order <ChevronRight size={12} />
                        </button>
                      </div>
                    )}

                    {/* Disabled overlay for expired */}
                    {isDelivered && days > 7 && (
                      <button
                        disabled
                        className="mt-2 w-full bg-gray-100 text-gray-400 text-xs py-2 rounded-full cursor-not-allowed"
                      >
                        Return Window Closed
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STEP: POLICY Q&A ── */}
        {step === "policy" && selectedOrder && (
          <div>
            <button
              onClick={() => setStep("orders")}
              className="flex items-center gap-2 text-sm text-[#6b665f] mb-6 hover:text-[#ba8245] transition-colors"
            >
              <ArrowLeft size={14} /> Back to Orders
            </button>

            <div className="bg-white border border-[#EAE4DC] rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Package size={16} className="text-[#ba8245]" />
                <span className="text-sm text-[#6b665f]">Order #{selectedOrder.id}</span>
              </div>
              <h2 className="font-serif text-2xl text-charcoal mb-6">Return Eligibility Check</h2>

              {/* Progress */}
              <div className="flex gap-2 mb-8">
                {POLICY_QUESTIONS.map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1 rounded-full transition-all ${i <= policyStep ? "bg-[#ba8245]" : "bg-[#EAE4DC]"}`}
                  />
                ))}
              </div>

              {invalidReason ? (
                <div>
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-5 mb-6">
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold mb-1">Return Not Eligible</div>
                      <p className="text-sm leading-relaxed">{invalidReason}</p>
                    </div>
                  </div>
                  <div className="text-sm text-[#6b665f] bg-[#f8f4ef] rounded-xl p-4 mb-6">
                    <strong>Our Policy:</strong> Siyaanish follows a no-return policy in line with our small-batch and ethically sourced production model. Returns are only accepted for products received damaged or with a manufacturing defect, reported within 7 days of delivery with clear proof.
                    <br/><br/>For questions, email <a href="mailto:info@siyaanish.com" className="text-[#ba8245]">info@siyaanish.com</a>
                  </div>
                  <button
                    onClick={() => { setInvalidReason(""); }}
                    className="w-full border border-[#ba8245] text-[#ba8245] py-3 rounded-full hover:bg-[#ba8245] hover:text-white transition-all text-sm"
                  >
                    Change Answer
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-charcoal font-medium mb-4">
                    Q{policyStep + 1} of {POLICY_QUESTIONS.length}: {POLICY_QUESTIONS[policyStep].question}
                  </p>
                  <div className="space-y-3">
                    {POLICY_QUESTIONS[policyStep].options.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleAnswer(POLICY_QUESTIONS[policyStep].id, opt.value)}
                        className="w-full text-left px-5 py-3.5 rounded-xl border border-[#EAE4DC] hover:border-[#ba8245] hover:bg-[#f8f4ef] transition-all text-sm text-charcoal flex items-center justify-between group"
                      >
                        {opt.label}
                        <ChevronRight size={14} className="text-[#ba8245] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP: CONFIRM ── */}
        {step === "confirm" && selectedOrder && (
          <div>
            <button
              onClick={() => { setPolicyStep(POLICY_QUESTIONS.length - 1); setStep("policy"); }}
              className="flex items-center gap-2 text-sm text-[#6b665f] mb-6 hover:text-[#ba8245] transition-colors"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <div className="bg-white border border-[#EAE4DC] rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle2 size={28} className="text-green-600" />
                <div>
                  <h2 className="font-serif text-2xl text-charcoal">Eligible for Return</h2>
                  <p className="text-sm text-[#6b665f]">Your order meets our return criteria</p>
                </div>
              </div>

              <div className="bg-[#f8f4ef] border-l-4 border-[#ba8245] rounded-r-xl p-5 mb-6">
                <p className="text-sm text-charcoal font-semibold mb-2">Return Summary</p>
                <p className="text-sm text-[#6b665f]"><strong>Order:</strong> #{selectedOrder.id}</p>
                <p className="text-sm text-[#6b665f]"><strong>Reason:</strong> {answers.reason === "damaged" ? "Product Damaged" : "Manufacturing Defect"}</p>
                <p className="text-sm text-[#6b665f]"><strong>Condition:</strong> Unused, original condition</p>
                <p className="text-sm text-[#6b665f]"><strong>Proof:</strong> Customer confirmed proof available</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-800">
                <strong>What happens next?</strong><br/>
                Once your return is submitted, our team will review it. If approved, eligible products will be replaced or store credit issued within 4–7 business days of the returned product being received. Please send proof of damage to <a href="mailto:info@siyaanish.com" className="underline">info@siyaanish.com</a>.
              </div>

              <button
                onClick={submitReturn}
                disabled={submitting}
                className="w-full bg-[#ba8245] text-white py-3 rounded-full shadow hover:bg-[#a8733d] transition-all uppercase tracking-wider text-sm font-medium disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit Return Request"}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP: DONE ── */}
        {step === "done" && (
          <div className="bg-white border border-[#EAE4DC] rounded-2xl p-10 shadow-sm text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="font-serif text-2xl text-charcoal mb-2">Return Request Submitted</h2>
            <p className="text-[#6b665f] text-sm mb-6">
              Your return for Order #{selectedOrder?.id} has been received. A confirmation has been sent to <strong>{email}</strong>.
            </p>
            {returnId && (
              <div className="bg-[#f8f4ef] border-l-4 border-[#ba8245] rounded-r-xl p-5 mb-6 text-left">
                <p className="text-sm text-[#5c3d1a] font-semibold mb-1">Return Reference</p>
                <p className="text-sm text-[#6b665f]">Return ID: <strong>#{returnId}</strong></p>
                <p className="text-sm text-[#6b665f]">Our team will review your request and assign a tracking ID shortly.</p>
              </div>
            )}
            <div className="text-sm text-[#6b665f] mb-6">
              Please email clear photos/videos of the damage to <a href="mailto:info@siyaanish.com" className="text-[#ba8245]">info@siyaanish.com</a> to complete your claim.
            </div>
            <a
              href="/"
              className="inline-block bg-[#ba8245] text-white py-3 px-8 rounded-full hover:bg-[#a8733d] transition-all text-sm uppercase tracking-wider"
            >
              Continue Shopping
            </a>
          </div>
        )}

      </div>
    </div>
  );
};

export default ReturnOrder;